/* ===========================================================================
   ROCKSOLAR product finder — what it saves
   ---------------------------------------------------------------------------
   assets/rs-finder-savings.js

   Loaded before the finder engine. It publishes window.RS_SAVE; the engine
   checks for it and, finding it, renders the savings block under every
   recommendation. Remove this file and the finder still works — the block and
   its teaser simply do not appear.

   This file holds no product data. It is rates, yields, tariffs and incentive
   rules by state, applied to whatever the finder recommended.

   EVERY FIGURE HERE IS AN ESTIMATE FOR COMPARISON, NOT A QUOTE. The page says
   so, and lists its own assumptions under the numbers, because a payback figure
   with hidden assumptions is worse than no payback figure.
   =========================================================================== */
(function () {
  "use strict";

  /* Residential electricity rates: EIA Electric Power Monthly, Table 5.6.A, March 2026 (¢/kWh).
     Yield = estimated annual kWh generated per kW installed, regional average. */
  var STATES = [
    ["AL","Alabama",17.15,1350],["AK","Alaska",27.17,950],["AZ","Arizona",15.59,1700],
    ["AR","Arkansas",13.63,1350],["CA","California",33.35,1550],["CO","Colorado",16.74,1500],
    ["CT","Connecticut",30.47,1200],["DE","Delaware",17.64,1300],["DC","District of Columbia",25.00,1300],
    ["FL","Florida",14.86,1400],["GA","Georgia",15.01,1400],["HI","Hawaii",42.23,1550],
    ["ID","Idaho",13.01,1350],["IL","Illinois",18.86,1250],["IN","Indiana",17.85,1200],
    ["IA","Iowa",13.42,1300],["KS","Kansas",15.34,1450],["KY","Kentucky",14.88,1250],
    ["LA","Louisiana",14.16,1350],["ME","Maine",28.32,1200],["MD","Maryland",22.20,1300],
    ["MA","Massachusetts",30.21,1200],["MI","Michigan",21.20,1150],["MN","Minnesota",15.08,1250],
    ["MS","Mississippi",16.30,1350],["MO","Missouri",13.44,1300],["MT","Montana",13.48,1300],
    ["NE","Nebraska",13.10,1350],["NV","Nevada",14.17,1650],["NH","New Hampshire",26.92,1200],
    ["NJ","New Jersey",23.49,1250],["NM","New Mexico",14.81,1700],["NY","New York",28.55,1150],
    ["NC","North Carolina",16.00,1400],["ND","North Dakota",11.95,1300],["OH","Ohio",18.78,1150],
    ["OK","Oklahoma",13.56,1450],["OR","Oregon",14.89,1150],["PA","Pennsylvania",20.92,1200],
    ["RI","Rhode Island",29.91,1200],["SC","South Carolina",16.45,1400],["SD","South Dakota",14.29,1350],
    ["TN","Tennessee",15.08,1300],["TX","Texas",16.39,1500],["UT","Utah",13.17,1500],
    ["VT","Vermont",24.11,1150],["VA","Virginia",17.05,1300],["WA","Washington",14.40,1050],
    ["WV","West Virginia",16.37,1200],["WI","Wisconsin",18.80,1200],["WY","Wyoming",13.59,1500]
  ];

  /* State residential solar income tax credits in force 2026: percent of system cost, capped. */
  var CREDIT = {
    SC:{p:25,cap:35000}, HI:{p:35,cap:5000}, NY:{p:25,cap:5000}, AZ:{p:25,cap:1000},
    UT:{p:25,cap:1600},  MA:{p:15,cap:1000}, NM:{p:10,cap:6000}
  };
  /* States exempting solar equipment from state sales tax, with the state-level rate avoided. */
  var SALESX = {AZ:5.6, CO:2.9, FL:6.0, IL:6.25, MD:6.0, MA:6.25, MN:6.875, NJ:6.625, NM:4.875, NY:4.0};
  /* SREC / REC markets paying for generation, $ per MWh (ranges vary by vintage and auction). */
  var SREC = {DC:[350,450], NJ:[200,250], IL:[75,90], MD:[40,60], MA:[50,150]};


  /* Export compensation. Full-retail net metering states credit exports at the retail rate;
     California NEM 3.0 and New Jersey's successor tariff pay well below it. Elsewhere we
     assume half retail, which is a middling avoided-cost tariff. */
  /* Pool season length. The kWh figures on the pump rows are full-year numbers —
     a 1.34 HP pump at roughly 8 hours a day for 365 days is about 3,000 kWh, which
     is what the row says. So the baseline is 12 months, and every state is scaled
     down from there. Without this a Maine pool was credited with the same running
     hours as a Hawaii pool, which made northern paybacks look about three times
     better than they are. These month counts are our own climate estimates, not
     sourced data — replace them with your own figures if you have them. */
  var POOL_BASE_MONTHS = 12;
  var POOL_MONTHS = {
    HI:12, FL:12, AZ:10, CA:10, TX:10, LA:10,
    NV:9, GA:9, AL:9, MS:9, SC:9, NM:9,
    OK:8, AR:8, NC:8, TN:8, VA:8, KY:8,
    MD:7, DE:7, MO:7, KS:7, DC:7, UT:7, NJ:7, WV:7,
    OR:6, CO:6, IN:6, IL:6, OH:6, PA:6, NY:6, NE:6,
    WA:5, CT:5, RI:5, MA:5, MI:5, WI:5, IA:5, SD:5, WY:5, ID:5,
    NH:4, VT:4, ME:4, MN:4, ND:4, MT:4,
    AK:3
  };
  function poolMonths(code){ return POOL_MONTHS[code] || 6; }

  var FULL_RETAIL = {MA:1,NY:1,MD:1,FL:1,CO:1,IL:1};
  var EXPORT_FIXED = {CA:0.06, NJ:0.105};
  var SELF_USE = 0.60;   /* share of generation consumed on site rather than exported */

  var GEN_COST = 0.45;   /* $/kWh, small petrol/diesel generator incl. fuel and servicing */
  /* Ceiling on avoided generator fuel. Beyond roughly this much annual running, nobody keeps
     burning fuel — they extend the grid instead. Generation above it is real, but it isn't
     displacing a cost, so we don't count it as a saving. */
  var GEN_CAP = 10000;
  var OFFGRID_USE = 0.70;/* fraction of off-grid generation actually consumed */
  var ESCALATION = 0.025;/* annual utility rate rise */
  var DEGRADE = 0.005;   /* annual panel output loss */
  var YEARS = 25;

  /* What a pump consumes in a year, for the pool and well savings rows.
     Pool: full-year kWh by horsepower, from about 8 hours a day of running;
     the finder's catalogue (rs-finder-catalog.js) carries the same table for
     its own rows, and the product page reads this one. Well: hp x 746 W x
     6 h/day x 300 days, as the finder's pump result uses. */
  var HP_KWH = [[0.5, 2000], [1.07, 2500], [1.34, 3000], [1.61, 3300], [2.14, 4000], [3.0, 4500]];
  function poolKwh(hp){
    if(hp == null) return 2500;
    if(hp <= HP_KWH[0][0]) return HP_KWH[0][1];
    for(var i = 1; i < HP_KWH.length; i++){
      if(hp <= HP_KWH[i][0]){
        var a = HP_KWH[i-1], b = HP_KWH[i];
        return Math.round(a[1] + (b[1]-a[1]) * (hp-a[0]) / (b[0]-a[0]));
      }
    }
    var last = HP_KWH[HP_KWH.length-1];
    return Math.round(last[1] * hp / last[0]);
  }
  function pumpKwh(hp){ return hp == null ? 0 : Math.round(hp * 746 * 6 * 300 / 1000); }

  var usd = function(n){ return "$" + Math.round(n).toLocaleString("en-US"); };
  var esc = function(s){ return String(s).replace(/[&<>"]/g,function(c){return {"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;"}[c];}); };

  function lifetime(annual, isUtility){
    var total = 0, v = annual;
    for(var y=0;y<YEARS;y++){
      total += v;
      v = v * (1 - DEGRADE) * (isUtility ? (1 + ESCALATION) : 1);
    }
    return total;
  }

  function compute(st, use, item){
    var rate = st[2]/100, yld = st[3], out = {cost:item.cost};
    if(use === "gridHome" || use === "gridBackup"){
      var exp = EXPORT_FIXED[st[0]] !== undefined ? EXPORT_FIXED[st[0]]
              : (FULL_RETAIL[st[0]] ? rate : rate * 0.5);
      out.exportRate = exp;
      out.blend = rate * SELF_USE + exp * (1 - SELF_USE);
      out.kwh = item.kw * yld;
      out.annual = out.kwh * out.blend;
      out.basis = Math.round(SELF_USE*100) + "% used on site at " + st[2].toFixed(2) + "¢, rest exported at " + (exp*100).toFixed(1) + "¢";
      out.util = true;
    } else if(use === "offgrid"){
      out.kwh = item.kw * yld * OFFGRID_USE;
      out.paid = Math.min(out.kwh, GEN_CAP);
      out.capped = out.kwh > GEN_CAP;
      out.annual = out.paid * GEN_COST;
      out.basis = out.capped
        ? "Generator fuel not burned, capped at " + GEN_CAP.toLocaleString("en-US") + " kWh/yr"
        : "Generator fuel not burned, at $" + GEN_COST.toFixed(2) + "/kWh";
      out.util = false;
    } else if(use === "pool"){
      out.months = poolMonths(st[0]);
      out.kwh = item.kwh * (out.months / POOL_BASE_MONTHS);
      out.annual = out.kwh * rate;
      out.basis = out.months + "-month pool season in " + esc(st[1]) + ", displaced at " + st[2].toFixed(2) + "¢/kWh";
      out.util = true;
    } else {
      out.kwh = item.kwh;
      out.annual = out.kwh * GEN_COST;
      out.basis = "Generator pumping cost avoided, at $" + GEN_COST.toFixed(2) + "/kWh";
      out.util = false;
    }
    out.payback = out.annual > 0 ? out.cost / out.annual : 0;
    out.lifetime = lifetime(out.annual, out.util);
    return out;
  }

  function incentives(st, use, item, r){
    var code = st[0], list = [], gridTied = (use === "gridHome" || use === "gridBackup");
    var c = CREDIT[code];
    if(c){
      var amt = Math.min(item.cost * c.p/100, c.cap);
      list.push({
        tag: gridTied ? "maybe" : "unlikely",
        title: st[1] + " solar income tax credit — " + c.p + "% of system cost, capped at " + usd(c.cap),
        body: gridTied
          ? "Worth up to " + usd(amt) + " on this system. Most state credits require the system to serve a residence you own and to be installed to code; some require a licensed installer. Check the current statute before relying on it."
          : "Written for residential solar electric systems on a home. " + (use==="pool"||use==="farm" ? "A pump is very unlikely to qualify." : "An off-grid installation may still qualify in some states, but it is not the typical case.") + " Confirm with your accountant.",
        amt: gridTied ? "up to " + usd(amt) : "—"
      });
    }
    if(SALESX[code]){
      var eligible = (use === "gridHome" || use === "offgrid");
      list.push({
        tag: eligible ? "yes" : "unlikely",
        title: st[1] + " sales tax exemption on solar equipment",
        body: eligible
          ? "Solar generating equipment is exempt from the " + SALESX[code] + "% state sales tax. This one comes off at checkout — no forms, no waiting for a refund."
          : "The exemption is written for solar electric generating equipment. Pumps are usually treated as ordinary goods and taxed normally.",
        amt: eligible ? "saves " + usd(item.cost * SALESX[code]/100) : "—"
      });
    }
    if(SREC[code]){
      list.push({
        tag: gridTied ? "maybe" : "no",
        title: st[1] + " SREC / REC income",
        body: gridTied
          ? "Pays roughly $" + SREC[code][0] + "–$" + SREC[code][1] + " per MWh generated. Requires an interconnected, metered system registered with the programme — and prices move with the auction."
          : "SRECs are paid on metered generation exported to the grid. An off-grid or pump system has no interconnection, so it cannot register.",
        amt: gridTied ? "$" + Math.round(r.kwh/1000*SREC[code][0]) + "–" + Math.round(r.kwh/1000*SREC[code][1]) + "/yr" : "—"
      });
    }
    if(gridTied){
      list.push({
        tag:"maybe",
        title:"Net metering with your utility",
        body:"Determines what your exported power is worth. Full retail credit in some states; well below retail in others — California's NEM 3.0 pays roughly 4–8¢/kWh against a 33¢ retail rate. This single variable can move payback by years, so check your own utility's tariff.",
        amt:"tariff-dependent"
      });
    }
    if(use === "farm"){
      list.push({
        tag:"maybe",
        title:"USDA REAP — up to 50% of project cost, max $1M",
        body:"For agricultural producers earning 50%+ of income from farming, and rural small businesses. Grant intake has been paused since 2025 with no published reopening date; guaranteed loans remain open year-round. Worth preparing an energy audit now so you're ready when it reopens.",
        amt:"grants paused"
      });
      list.push({
        tag:"maybe",
        title:"Business tax treatment",
        body:"If the pump is a farm business asset, the purchase may be depreciable, and the commercial clean energy credit (48E) may apply to qualifying generation equipment — but that one requires construction to have commenced by 4 July 2026, or the system placed in service by 31 December 2027. Your accountant, not us.",
        amt:"varies"
      });
    }
    list.push({
      tag:"no",
      title:"Federal residential credit (Section 25D) — no longer available",
      body:"The 30% federal residential clean energy credit terminated on 31 December 2025. Systems had to be installed on or before that date. It does not apply to anything bought in 2026, and no figure on this page includes it.",
      amt:"$0"
    });
    return list;
  }

  function assumptions(st, use, item, r){
    var a = [
      "Electricity rate: <strong>" + st[2].toFixed(2) + "¢/kWh</strong>, the " + esc(st[1]) + " residential average (EIA Electric Power Monthly, Table 5.6.A, March 2026). Your own tariff is what actually matters.",
      "Solar yield: <strong>" + st[3] + " kWh per kW installed per year</strong>, a regional average for " + esc(st[1]) + ". A shaded or north-facing roof will produce materially less.",
      "Panel output declines <strong>" + (DEGRADE*100).toFixed(1) + "% per year</strong> over the " + YEARS + "-year figure."
    ];
    if(r.util){
      a.push("Electricity prices rise <strong>" + (ESCALATION*100).toFixed(1) + "% per year</strong>. If they rise faster you save more; if they fall, less.");
      if(use === "gridHome" || use === "gridBackup"){
        a.push("Assumes <strong>" + Math.round(SELF_USE*100) + "% of generation is used in the house</strong> at the full retail rate, and the remaining " + Math.round((1-SELF_USE)*100) + "% is exported at <strong>" + (r.exportRate*100).toFixed(1) + "¢/kWh</strong>" + (EXPORT_FIXED[st[0]] !== undefined ? " — " + esc(st[1]) + "'s current export tariff, which is far below retail." : (FULL_RETAIL[st[0]] ? " — full retail credit under " + esc(st[1]) + " net metering." : " — assumed at half retail, since " + esc(st[1]) + " has no full-retail guarantee.")) + " A battery raises the self-use share and the saving with it.");
      }
    } else {
      a.push("Generator running cost taken as <strong>$" + GEN_COST.toFixed(2) + "/kWh</strong>, covering fuel plus servicing for a small petrol or diesel set. Diesel at scale is cheaper; a small petrol generator is often dearer.");
      if(use === "offgrid"){
        a.push("Assumes <strong>" + Math.round(OFFGRID_USE*100) + "%</strong> of generation is actually consumed. Off-grid arrays are deliberately oversized for bad weather, so some output goes unused in summer.");
        if(r.capped) a.push("Avoided fuel is <strong>capped at " + GEN_CAP.toLocaleString("en-US") + " kWh a year</strong>. This system generates about " + Math.round(r.kwh).toLocaleString("en-US") + " kWh, but past roughly that point nobody keeps running a generator — they pay to extend the grid instead. We don't count savings against a cost you were never going to carry.");
      }
      a.push("Excludes the cost of <strong>extending a utility line</strong> to the site. Where that quote runs tens of thousands, it usually dominates every other number here.");
    }
    if(use === "pool"){
      a.push("Assumes the pump it replaces is a <strong>single-speed unit running about 8 hours a day in season</strong>. If you already run a variable-speed pump, the saving is much smaller.");
      if(r && r.payback > 0 && r.payback < 2) a.push("A payback under two years is unusual, and it is real only if all three assumptions above hold — the old pump was single-speed, it ran those hours, and your tariff is near the state average. Check it against your own power bill before you rely on it.");
      a.push("Season length is taken as <strong>" + poolMonths(st[0]) + " months a year</strong> in " + esc(st[1]) + ". A heated or indoor pool runs longer and saves more; a pool you close early saves less. This is our own climate estimate, not a published figure.");
    }
    a.push("Excludes installation labour, permits, <strong>racking and mounting hardware</strong> (bought separately to suit your roof), and any electrical upgrade your property needs.");
    if(use === "gridBackup"){ a.push("Backup is <strong>partial home, not whole home</strong>. The transfer switch feeds a sub-panel of essential circuits — lights, fridge, well pump, a few outlets. Large loads like central air conditioning or an electric range usually stay off the backed-up side.");
      a.push("The value of <strong>staying powered during an outage</strong> is not priced in at all. If outages cost you a freezer of food or a day of lost work, that number is yours to add."); }
    a.push("Battery replacement is <strong>not</strong> included in the " + YEARS + "-year figure. LiFePO4 packs typically last 10–15 years.");
    return a;
  }

  window.RS_SAVE = {
    STATES:STATES, compute:compute, incentives:incentives, assumptions:assumptions,
    SELF_USE:SELF_USE, GEN_COST:GEN_COST, YEARS:YEARS,
    usd:usd, esc:esc,
    poolKwh:poolKwh, pumpKwh:pumpKwh
  };
})();
